import { z } from 'zod';
import { filePath, isoDateTime, normalizePath, pathProblem, slug } from './common.js';

/**
 * Pages (docs/PAGES.md): AI-written HTML hosted in a publication. A page is a folder with `page.json` and an entry
 * (default `index.html`) plus whatever files it needs. The platform knows no page types.
 */

export const PAGE_JSON = 'page.json';
export const PAGE_FORMAT = 'page/1';

/** A page id: unique within a revision, used in URLs and by `p2b.openPage`. */
export const pageId = slug;

/** A folder inside the workspace, stored with a trailing slash ("models/R/reports/arekstrar/"). Never the root. */
export const folderPath = z
  .string()
  .transform((s) => {
    const n = normalizePath(s).replace(/\/+$/, '');
    return n === '' ? '' : `${n}/`;
  })
  .superRefine((s, ctx) => {
    if (s === '') {
      ctx.addIssue({ code: 'custom', message: 'a page folder cannot be the workspace root' });
      return;
    }
    const problem = pathProblem(s.slice(0, -1));
    if (problem) ctx.addIssue({ code: 'custom', message: problem });
  });

export const pageAudience = z.enum(['client', 'internal']);
export type PageAudience = z.infer<typeof pageAudience>;

const madeFrom = z.looseObject({
  /** The revision label the page describes (a page is frozen with that revision or a later one). */
  revision: z.string().max(120).optional(),
  /** The model build hash (model.js SHA-256) the page was made from. */
  build: z.string().max(128).optional(),
});

/** A path relative to the page folder (no leading slash, no URL). `..` is allowed: data may sit beside the page. */
const relPath = z
  .string()
  .min(1)
  .max(1024)
  .refine((s) => !s.startsWith('/') && !/^[a-z][a-z0-9+.-]*:/i.test(s), 'a path relative to the page folder');

/** `page.json`, written beside the entry. Unknown keys are kept (forward compatibility). */
export const pageJson = z.looseObject({
  $schema: z.string().optional(),
  p2b: z.literal(PAGE_FORMAT),
  id: pageId,
  title: z.string().min(1).max(200),
  /** The HTML file the page opens, relative to the folder. */
  entry: relPath.default('index.html'),
  /** `client` pages may be published; `internal` pages are for Mynstra and reviewers. */
  audience: pageAudience.default('client'),
  description: z.string().max(2000).optional(),
  madeFrom: madeFrom.optional(),
  author: z.string().max(200).optional(),
  /**
   * The files that belong to the page: globs relative to the folder, which may climb out of it ("../arekstrar.json").
   * Default: everything in the folder. The entry is always included. Only these files are served.
   */
  files: z.array(relPath).min(1).max(200).default(['**']),
});
export type PageJson = z.infer<typeof pageJson>;
export type PageJsonInput = z.input<typeof pageJson>;

/** A page frozen in a revision manifest: workspace paths, data globs resolved to files. */
export const manifestPage = z.object({
  id: pageId,
  title: z.string().min(1).max(200),
  /** The entry, a path in the manifest's `files`. */
  entry: filePath,
  /** The page folder (where page.json is). */
  root: folderPath,
  audience: pageAudience.default('client'),
  /** Every file the page serves (paths in the manifest's `files`), the entry included. */
  files: z.array(filePath).min(1),
  description: z.string().max(2000).optional(),
  madeFrom: madeFrom.optional(),
  author: z.string().max(200).optional(),
});
export type ManifestPage = z.infer<typeof manifestPage>;

/** A page as a publication lists it. */
export const publishedPage = z.object({
  id: pageId,
  title: z.string(),
  /** The shell URL (app origin) that shows the page. */
  url: z.string(),
  entry: z.string(),
});
export type PublishedPage = z.infer<typeof publishedPage>;

/** Join a page folder and a path relative to it; null when it climbs above the workspace root. */
export function resolveIn(folder: string, rel: string): string | null {
  const parts = folder.split('/').filter(Boolean);
  for (const seg of normalizePath(rel).split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      if (!parts.length) return null;
      parts.pop();
    } else parts.push(seg);
  }
  return parts.join('/');
}

/** The relative path from folder `from` ("a/b/") to workspace path `to` ("a/c/x.html" → "../c/x.html"). */
export function relativeTo(from: string, to: string): string {
  const a = from.split('/').filter(Boolean);
  const b = to.split('/');
  let i = 0;
  while (i < a.length && i < b.length - 1 && a[i] === b[i]) i++;
  return '../'.repeat(a.length - i) + b.slice(i).join('/');
}

/**
 * The files of a page: its `files` globs resolved against the workspace (`matchGlob` takes a workspace-relative
 * glob), plus the entry and page.json. `unresolved` lists globs that matched nothing.
 */
export function resolvePageFiles(
  root: string,
  pj: Pick<PageJson, 'entry' | 'files'>,
  matchGlob: (glob: string) => string[],
): { files: string[]; unresolved: string[] } {
  const out = new Set<string>();
  const unresolved: string[] = [];
  for (const g of pj.files) {
    const abs = resolveIn(root, g);
    const hits = abs === null ? [] : matchGlob(abs);
    if (!hits.length) unresolved.push(g);
    for (const h of hits) out.add(h);
  }
  const entry = resolveIn(root, pj.entry);
  if (entry) out.add(entry);
  out.add(`${root}${PAGE_JSON}`);
  return { files: [...out].sort(), unresolved };
}

/** Turn a parsed `page.json` at workspace path `jsonPath` into its manifest form, resolving its file globs. */
export function toManifestPage(
  jsonPath: string,
  pj: PageJson,
  matchGlob: (glob: string) => string[],
): { page: ManifestPage; unresolved: string[] } {
  const root = jsonPath.includes('/') ? jsonPath.slice(0, jsonPath.lastIndexOf('/') + 1) : '';
  const { files, unresolved } = resolvePageFiles(root, pj, matchGlob);
  const page = manifestPage.parse({
    id: pj.id,
    title: pj.title,
    entry: resolveIn(root, pj.entry) ?? pj.entry,
    root,
    audience: pj.audience,
    files: files.filter((f) => matchGlob(f).length > 0),
    ...(pj.description ? { description: pj.description } : {}),
    ...(pj.madeFrom ? { madeFrom: pj.madeFrom } : {}),
    ...(pj.author ? { author: pj.author } : {}),
  });
  return { page, unresolved };
}

/** Libraries the platform hosts at `/lib/<name>@<version>/<path>` (jsDelivr's npm layout). Pages load nothing else. */
export const HOSTED_LIBRARIES = [
  {
    name: 'echarts',
    version: '6.1.0',
    paths: ['dist/'],
    main: 'dist/echarts.min.js',
    note: 'charts (global `echarts`)',
  },
  {
    name: 'three',
    version: '0.160.0',
    paths: ['build/', 'examples/jsm/'],
    main: 'build/three.module.js',
    note: '3D (ES modules; addons under examples/jsm/)',
  },
  {
    name: 'p2b',
    version: '1',
    paths: ['p2b.js'],
    main: 'p2b.js',
    note: 'the bridge to the platform (global `p2b`)',
  },
] as const;

export type HostedLibrary = (typeof HOSTED_LIBRARIES)[number];

/** `/lib/<name>@<version>/<path>` for a hosted library, or null. */
export function hostedLibraryPath(name: string, path?: string): string | null {
  const lib = HOSTED_LIBRARIES.find((l) => l.name === name);
  return lib ? `/lib/${lib.name}@${lib.version}/${path ?? lib.main}` : null;
}

/** Size limits for pages (checked by `p2b page check` and at publish; the content route streams everything). */
export const PAGE_LIMITS = {
  entryBytes: 32 * 1024 * 1024,
  fileBytes: 256 * 1024 * 1024,
  pageBytes: 512 * 1024 * 1024,
  /** MCP `put_page`: files sent inline. */
  inlineBytes: 20 * 1024 * 1024,
} as const;

/** `POST /api/projects/:id/page-sessions`: the shell asks for a content base for one publication (or "draft"). */
export const pageSessionRequest = z.object({
  publication: z.union([z.literal('draft'), z.string().min(1).max(64)]),
});
export type PageSessionRequest = z.infer<typeof pageSessionRequest>;

export const pageSession = z.object({
  /** `<content origin>/s/<token>/`; a page file's URL is this plus its workspace path. */
  contentBase: z.string(),
  expiresAt: isoDateTime,
  org: slug,
  project: slug,
  projectName: z.string(),
  publication: z.string(),
  revision: z.string().nullable(),
  title: z.string().nullable(),
  pages: z.array(publishedPage.extend({ root: z.string() })),
  /** The publication's model viewer (existing route), when it has one. */
  viewer: z.object({ url: z.string(), root: z.string(), entry: z.string() }).nullable(),
});
export type PageSession = z.infer<typeof pageSession>;

/** The sandbox every page runs in: an opaque origin (no cookies, no storage, no same-origin calls). */
export const PAGE_SANDBOX =
  'allow-scripts allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals';

/**
 * The content policy for one page session (docs/PAGES.md §3.2): `content` is the pages origin, `appOrigin` the app
 * that may frame it (omit for a local preview, where pages open on their own).
 */
export function pageCsp(content: string, token: string, appOrigin?: string): string {
  const own = `${content}/s/${token}/`;
  const lib = `${content}/lib/`;
  return [
    `sandbox ${PAGE_SANDBOX}`,
    "default-src 'none'",
    `script-src 'unsafe-inline' ${own} ${lib}`,
    `style-src 'unsafe-inline' ${own} ${lib}`,
    `img-src data: blob: ${own}`,
    `font-src data: ${own} ${lib}`,
    `media-src data: blob: ${own}`,
    `connect-src ${own}`,
    'worker-src blob:',
    "form-action 'none'",
    "base-uri 'none'",
    ...(appOrigin ? [`frame-ancestors ${appOrigin}`] : []),
  ].join('; ');
}

const BRIDGE_TAG = '<script src="/lib/p2b@1/p2b.js"></script>';

/** Add the bridge to an HTML document as it is served (stored bytes unchanged; a page may load it itself too). */
export function injectBridge(html: string): string {
  if (html.includes('/lib/p2b@1/p2b.js')) return html;
  const head = /<head\b[^>]*>/i.exec(html);
  if (head)
    return html.slice(0, head.index + head[0].length) + BRIDGE_TAG + html.slice(head.index + head[0].length);
  const doctype = /^\s*<!doctype[^>]*>/i.exec(html);
  if (doctype) return doctype[0] + BRIDGE_TAG + html.slice(doctype[0].length);
  return BRIDGE_TAG + html;
}
